import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { Role } from './types';

/** All-time counters from the player's own leaderboard doc. */
export type ProfileStats = {
  games: number;
  wins: number;
  losses: number;
  mvp: number;
  /** Consecutive UTC days with at least one finished game, 0 if broken. */
  streak: number;
  bestStreak: number;
};

/** One row of the player's match history, projected from `games/{seed}`. */
export type MatchRecord = {
  gameId: string;
  role: Role | null;
  characterId: string;
  won: boolean;
  mvp: boolean;
  playerCount: number;
  endedAtMs: number | null;
};

/** UTC calendar day like "2026-07-11" — mirrors the server's streak keying. */
function utcDay(offsetDays = 0): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

export async function fetchProfileStats(uid: string): Promise<ProfileStats> {
  const snap = await getDoc(doc(db, 'leaderboard', uid));
  const x = snap.exists() ? snap.data() : {};
  const n = (v: unknown) => (typeof v === 'number' ? v : 0);
  // A streak is only alive if the last counted day is today or yesterday.
  const alive = x.streakDay === utcDay() || x.streakDay === utcDay(-1);
  return {
    games: n(x.games),
    wins: n(x.wins),
    losses: n(x.losses),
    mvp: n(x.mvp),
    streak: alive ? n(x.streak) : 0,
    bestStreak: n(x.bestStreak),
  };
}

/**
 * The player's most recent matches, newest first. The archive doc holds every
 * player's post-game projection; we extract only the caller's own line. The
 * array-contains filter on playerUids is what satisfies the security rule.
 */
export async function fetchRecentMatches(uid: string, top = 20): Promise<MatchRecord[]> {
  const q = query(
    collection(db, 'games'),
    where('playerUids', 'array-contains', uid),
    orderBy('endedAt', 'desc'),
    limit(top),
  );
  const snap = await getDocs(q);
  const rows: MatchRecord[] = [];
  for (const d of snap.docs) {
    const x = d.data();
    const players = Array.isArray(x.players) ? x.players : [];
    const me = players.find((p) => p && p.uid === uid);
    if (!me) continue;
    rows.push({
      gameId: d.id,
      role: typeof me.role === 'string' ? (me.role as Role) : null,
      characterId: typeof me.characterId === 'string' ? me.characterId : '',
      won: me.won === true,
      mvp: me.mvp === true,
      playerCount: typeof x.playerCount === 'number' ? x.playerCount : players.length,
      endedAtMs: x.endedAt ? (x.endedAt as Timestamp).toMillis() : null,
    });
  }
  return rows;
}
